/* eslint-disable react/jsx-props-no-spreading */
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Box, Tooltip } from '@mui/material'
import TableMui from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
  useFlexLayout,
  useRowSelect,
  useFilters
} from 'react-table'

import { enhancedTableStyles as styles } from 'src/components/shared/EnhancedTable/EnhancedTableStyles'
import { ArrowIcon, DocumentIcon } from 'src/components/shared/ui/Icons'
import { Filter, FilterType } from './FilterColumnHeader/FilterColumnHeader'
import TableFooter from './TableFooter'
import TableToolbar from './TableToolbar'

export function ToolTipHeader({ column }: { column: any }): JSX.Element {
  const [open, setOpen] = useState<boolean>(false)

  return (
    <Tooltip
      open={open}
      arrow
      title={column.Header}
      placement="top"
      onMouseOver={ev => {
        const title: HTMLDivElement = ev.target as HTMLDivElement
        setOpen(title.scrollWidth > title.clientWidth)
      }}
      onMouseLeave={() => setOpen(false)}
    >
      <Box sx={styles.headerCellText}>{column.render('Header')}</Box>
    </Tooltip>
  )
}

type Props<T> = {
  columns: any[]
  data: any[]
  isDocumentSection?: boolean
  fixed?: boolean
  title?: string
  toolbar?: boolean
  noRegistersMessage?: string
  noRegistersMessageRequired?: boolean
  noRegisterIcon?: boolean
  required?: boolean
  footer?: boolean
  selectRows?: boolean
  hoverRows?: boolean
  multiSelect?: boolean
  variation?: 'default' | 'modal' | 'form'
  rowSelect?: any
  onRowSelectStateChange?: (row: T) => void
  search?: string
  pSize?: number
  error?: string
}

function EnhancedTable<T>({
  columns,
  data,
  title,
  toolbar = true,
  isDocumentSection = false,
  fixed = false,
  noRegistersMessage,
  noRegistersMessageRequired = false,
  noRegisterIcon = false,
  required = false,
  footer = true,
  hoverRows = false,
  selectRows = false,
  multiSelect = true,
  variation = 'default',
  rowSelect,
  onRowSelectStateChange,
  search,
  pSize = 10,
  error = ''
}: Props<T>): JSX.Element {
  const filterTypes = useMemo(
    () => ({
      customFilter: (rows, id, filterValue: Filter) => {
        const { type, query } = filterValue

        if (query.trim().length > 0) {
          return rows.filter(row => {
            const rowValue = row.values[id]

            if (rowValue === undefined) {
              return true
            }

            return type === FilterType.START_WITH
              ? String(rowValue)
                  .toLowerCase()
                  .startsWith(String(query).toLowerCase())
              : String(rowValue)
                  .toLowerCase()
                  .includes(String(query).toLowerCase())
          })
        }

        return rows
      }
    }),
    []
  )

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    gotoPage,
    setPageSize,
    setGlobalFilter,
    selectedFlatRows,
    state: { pageIndex, pageSize, globalFilter }
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: {
        pageSize: pSize
      },
      stateReducer: (newState, action) => {
        if (action.type === 'toggleRowSelected' && !multiSelect) {
          newState.selectedRowIds = {
            [action.id]: true
          }
        }
        return newState
      }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useFlexLayout,
    useRowSelect
  )

  const { t } = useTranslation()

  const formVariationClasses = classNames({
    'modal-variation': variation === 'modal',
    'form-variation': variation === 'form',
    'required-table': required,
    'empty-table': data.length === 0,
    'fixed-table': fixed,
    'document-variation': isDocumentSection
  })

  const norRegisterMessageRequiredClasses = classNames({
    'no-register-message-required': noRegistersMessageRequired
  })

  function showEmpty(): ReactElement | null {
    if (rows && rows.length === 0) {
      if (noRegisterIcon) {
        return (
          <Box component="tr" className="no-register-message-container" sx={styles.noRegistersBox}>
            <Box component="td" sx={styles.noRegistersBoxSvg}>
              <DocumentIcon />
            </Box>
            <Box component="td" sx={styles.noRegistersBoxSvg}>
              {noRegistersMessage}
            </Box>
          </Box>
        )
      }

      return (
        <Box component="tr" className="no-register-message-container" sx={styles.noRegistersBox}>
          <td className={norRegisterMessageRequiredClasses}>{noRegistersMessage}</td>
        </Box>
      )
    }

    return null
  }

  function showRequiredContainer(): ReactElement | null {
    if (required || error) {
      return (
        <Box component="span" sx={styles.requiredText}>
          {error?.length > 0 ? error : t('form.validation.required.required_table')}
        </Box>
      )
    }
    return null
  }

  const isItemSelected = row => selectedFlatRows.findIndex(d => d.index === row.index) !== -1

  useEffect(() => {
    onRowSelectStateChange?.(selectedFlatRows)
  }, [onRowSelectStateChange, selectedFlatRows, rowSelect])

  useEffect(() => {
    setGlobalFilter(search)
  }, [search, setGlobalFilter])

  return (
    <>
      <TableContainer className={formVariationClasses} sx={styles.table}>
        {toolbar && (
          <TableToolbar title={title} setSearchText={setGlobalFilter} searchText={globalFilter} />
        )}
        <TableMui {...getTableProps()} sx={styles.tableMui}>
          <TableHead>
            {headerGroups.map(headerGroup => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <TableCell
                    sx={styles.headerCell}
                    {...(column.id === 'selection'
                      ? column.getHeaderProps()
                      : column.getHeaderProps(
                          column.getSortByToggleProps({
                            title: ''
                          })
                        ))}
                  >
                    <Box sx={styles.headerCellText} flex="0 1 auto">
                      <ToolTipHeader column={column} />
                    </Box>
                    {column.canFilter && column?.filter ? column.render('Filter') : null}
                    {column.id !== 'selection' ? (
                      <TableSortLabel
                        sx={styles.headerCellSort}
                        active={column.isSorted}
                        // react-table has a unsorted state which is not treated here
                        direction={column.isSortedDesc ? 'desc' : 'asc'}
                        IconComponent={() => (
                          <ArrowIcon
                            sx={{
                              transform: `rotate(${column.isSortedDesc ? '0deg' : '180deg'})`
                            }}
                          />
                        )}
                      />
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {showEmpty()}
            {page.map(row => {
              prepareRow(row)
              const isSelected = isItemSelected(row)
              return (
                <TableRow
                  hover={hoverRows}
                  selected={isSelected ?? undefined}
                  {...row.getRowProps({
                    onClick: () => {
                      if (selectRows) {
                        row.toggleRowSelected()
                      }
                    }
                  })}
                  role={selectRows ? 'checkbox' : 'row'}
                  aria-checked={isSelected}
                >
                  {row.cells.map(cell => {
                    return (
                      <TableCell sx={styles.bodyCell} {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
          {/* FOOTER */}
          {footer && (
            <TableFooter
              data={rows}
              pageSize={pageSize}
              pageIndex={pageIndex}
              gotoPage={gotoPage}
              setPageSize={setPageSize}
            />
          )}
        </TableMui>
      </TableContainer>
      {/* REQUIRED TEXT */}
      {showRequiredContainer()}
    </>
  )
}

export default EnhancedTable
